import React, { useState, useEffect } from 'react';
import { supabase, BLOOD_GROUPS } from '../lib/supabase';

const URGENCY_LABELS = { critical: '🚨 അടിയന്തരം', urgent: '⚡ ഉടൻ', normal: '📋 സാധാരണ' };
const URGENCY_CLASSES = { critical: 'urgency-critical', urgent: 'urgency-urgent', normal: 'urgency-normal' };

// Admin WhatsApp numbers (international format, no + or spaces)
const ADMIN_NUMBERS = ['916238014005', '918606470184'];

function notifyAdmins(requestData) {
  const msg = encodeURIComponent(
    `🆘 *പുതിയ രക്ത അഭ്യർത്ഥന*\n\n` +
    `🩸 ഗ്രൂപ്പ്: ${requestData.blood_group}\n` +
    `👤 രോഗി: ${requestData.patient_name}\n` +
    `🏥 ആശുപത്രി: ${requestData.hospital}\n` +
    `📞 ബന്ധം: ${requestData.contact_phone}\n` +
    `⚡ അടിയന്തരത: ${URGENCY_LABELS[requestData.urgency]}\n` +
    (requestData.units_needed > 1 ? `📦 യൂണിറ്റ്: ${requestData.units_needed}\n` : '') +
    (requestData.message ? `📝 ${requestData.message}\n` : '') +
    `\nതോട്ടക്കാട് രക്തദാന ഡയറക്ടറി — tbda.vercel.app`
  );
  ADMIN_NUMBERS.forEach((number, i) => {
    setTimeout(() => {
      window.open(`https://wa.me/${number}?text=${msg}`, '_blank', 'noopener,noreferrer');
    }, i * 800);
  });
}

function notifyAdminDonorVolunteer(request, donorName, donorPhone, donorBloodGroup) {
  const msg = encodeURIComponent(
    `🙋 *ദാതാവ് സ്വയം മുന്നോട്ട് വന്നിരിക്കുന്നു!*\n\n` +
    `━━━━━━━━━━━━━━━\n` +
    `👤 ദാതാവ്: ${donorName}\n` +
    `🩸 ഗ്രൂപ്പ്: ${donorBloodGroup}\n` +
    `📞 ഫോൺ: ${donorPhone}\n` +
    `━━━━━━━━━━━━━━━\n` +
    `🏥 അഭ്യർത്ഥന: ${request.patient_name}\n` +
    `🩸 ആവശ്യം: ${request.blood_group}\n` +
    `🏥 ആശുപത്രി: ${request.hospital}\n` +
    `⚡ ${URGENCY_LABELS[request.urgency]}\n` +
    `━━━━━━━━━━━━━━━\n` +
    `ദാതാവിനെ ബന്ധപ്പെടൂ, ദാനം സ്ഥിരീകരിക്കൂ, tbda.vercel.app/admin-ൽ പോയിന്റ് നൽകൂ.`
  );
  ADMIN_NUMBERS.forEach((number, i) => {
    setTimeout(() => {
      window.open(`https://wa.me/${number}?text=${msg}`, '_blank', 'noopener,noreferrer');
    }, i * 800);
  });
}

export default function Request() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Volunteer modal state
  const [volunteerModal, setVolunteerModal] = useState(null); // holds request object
  const [volunteerForm, setVolunteerForm] = useState({ name: '', phone: '', blood_group: '' });
  const [volunteerErrors, setVolunteerErrors] = useState({});
  const [volunteerSent, setVolunteerSent] = useState(false);

  const [form, setForm] = useState({
    patient_name: '', blood_group: '', hospital: '', contact_phone: '',
    units_needed: 1, urgency: 'urgent', message: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => { fetchRequests(); }, []);

  async function fetchRequests() {
    setLoading(true);
    const { data } = await supabase
      .from('blood_requests')
      .select('id, patient_name, blood_group, hospital, units_needed, urgency, message, created_at, is_fulfilled')
      .eq('is_fulfilled', false)
      .order('created_at', { ascending: false });
    setRequests(data || []);
    setLoading(false);
  }

  function validate() {
    const e = {};
    if (!form.patient_name.trim()) e.patient_name = 'രോഗിയുടെ പേര് ആവശ്യമാണ്';
    if (!form.blood_group) e.blood_group = 'രക്ത ഗ്രൂപ്പ് ആവശ്യമാണ്';
    if (!form.hospital.trim()) e.hospital = 'ആശുപത്രിയുടെ പേര് ആവശ്യമാണ്';
    if (!form.contact_phone.trim()) e.contact_phone = 'ഫോൺ നമ്പർ ആവശ്യമാണ്';
    else if (!/^[6-9]\d{9}$/.test(form.contact_phone.replace(/\s/g, ''))) e.contact_phone = 'ശരിയായ ഫോൺ നമ്പർ നൽകൂ';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitLoading(true);
    try {
      const { error } = await supabase.from('blood_requests').insert([{
        ...form,
        patient_name: form.patient_name.trim(),
        hospital: form.hospital.trim(),
        contact_phone: form.contact_phone.trim(),
        message: form.message.trim() || null,
      }]);
      if (error) throw error;
      notifyAdmins({ ...form, patient_name: form.patient_name.trim(), hospital: form.hospital.trim(), contact_phone: form.contact_phone.trim() });
      setSubmitSuccess(true);
      fetchRequests();
    } catch (err) {
      console.error('Request submit error:', err);
      setErrors({ submit: 'സമർപ്പണം പരാജയപ്പെട്ടു. വീണ്ടും ശ്രമിക്കൂ.' });
    } finally {
      setSubmitLoading(false);
    }
  }

  async function markFulfilled(id) {
    await supabase.from('blood_requests').update({ is_fulfilled: true }).eq('id', id);
    fetchRequests();
  }

  function update(k, v) {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => { const n = { ...e }; delete n[k]; return n; });
  }

  function updateVolunteer(k, v) {
    setVolunteerForm(f => ({ ...f, [k]: v }));
    if (volunteerErrors[k]) setVolunteerErrors(e => { const n = { ...e }; delete n[k]; return n; });
  }

  function validateVolunteer() {
    const e = {};
    if (!volunteerForm.name.trim()) e.name = 'പേര് ആവശ്യമാണ്';
    if (!volunteerForm.blood_group) e.blood_group = 'രക്ത ഗ്രൂപ്പ് ആവശ്യമാണ്';
    if (!volunteerForm.phone.trim()) e.phone = 'ഫോൺ നമ്പർ ആവശ്യമാണ്';
    else if (!/^[6-9]\d{9}$/.test(volunteerForm.phone.replace(/\s/g, ''))) e.phone = 'ശരിയായ നമ്പർ നൽകൂ';
    return e;
  }

  function handleVolunteer() {
    const errs = validateVolunteer();
    if (Object.keys(errs).length) { setVolunteerErrors(errs); return; }
    notifyAdminDonorVolunteer(volunteerModal, volunteerForm.name.trim(), volunteerForm.phone.trim(), volunteerForm.blood_group);
    setVolunteerSent(true);
  }

  function timeAgo(date) {
    const diff = (Date.now() - new Date(date)) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)} മിനിറ്റ് മുമ്പ്`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} മണിക്കൂർ മുമ്പ്`;
    return `${Math.floor(diff / 86400)} ദിവസം മുമ്പ്`;
  }

  if (submitSuccess) {
    return (
      <div className="page-sm">
        <div className="success-screen">
          <div className="success-icon">📢</div>
          <div className="success-title">അഭ്യർത്ഥന പോസ്റ്റ് ചെയ്തു</div>
          <p className="success-sub">
            {form.blood_group} ഗ്രൂപ്പ് ദാതാക്കൾ ഉടൻ ബന്ധപ്പെടും. ഫോൺ ഓൺ ആയിരിക്കൂ.<br/>
            <strong style={{ color: 'var(--red)' }}>📲 രണ്ട് അഡ്മിൻമാർക്കും WhatsApp അറിയിപ്പ് അയച്ചിട്ടുണ്ട്.</strong>
          </p>
          <button className="btn btn-primary"
            onClick={() => { setSubmitSuccess(false); setShowForm(false); setForm({ patient_name: '', blood_group: '', hospital: '', contact_phone: '', units_needed: 1, urgency: 'urgent', message: '' }); }}>
            അഭ്യർത്ഥനകൾ കാണൂ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">

      {/* Volunteer modal */}
      {volunteerModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="card" style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
            <button onClick={() => { setVolunteerModal(null); setVolunteerSent(false); setVolunteerForm({ name: '', phone: '', blood_group: '' }); setVolunteerErrors({}); }}
              style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-3)' }}>✕</button>

            {volunteerSent ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: 48 }}>🙏</div>
                <div style={{ fontWeight: 800, fontSize: 18, marginTop: 12, color: 'var(--red)' }}>നന്ദി! അഡ്മിൻ ഉടൻ ബന്ധപ്പെടും</div>
                <p style={{ color: 'var(--text-2)', marginTop: 8, fontSize: 14 }}>ദാനം പൂർത്തിയായാൽ അഡ്മിൻ നിങ്ങൾക്ക് <strong>+50 പോയിന്റ്</strong> നൽകും.</p>
                <button className="btn btn-primary" style={{ marginTop: 16 }}
                  onClick={() => { setVolunteerModal(null); setVolunteerSent(false); setVolunteerForm({ name: '', phone: '', blood_group: '' }); }}>
                  ശരി
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4 }}>🙋 ഞാൻ ദാനം ചെയ്യാം</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16 }}>
                  {volunteerModal.blood_group} · {volunteerModal.patient_name} · {volunteerModal.hospital}
                </div>

                <div className="form-group">
                  <label className="form-label">നിങ്ങളുടെ പേര് *</label>
                  <input className="form-input" type="text" placeholder="പൂർണ്ണ പേര്"
                    value={volunteerForm.name} onChange={e => updateVolunteer('name', e.target.value)} />
                  {volunteerErrors.name && <div className="form-error">{volunteerErrors.name}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">രക്ത ഗ്രൂപ്പ് *</label>
                  <select className="form-select" value={volunteerForm.blood_group} onChange={e => updateVolunteer('blood_group', e.target.value)}>
                    <option value="">തിരഞ്ഞെടുക്കൂ</option>
                    {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  {volunteerErrors.blood_group && <div className="form-error">{volunteerErrors.blood_group}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">ഫോൺ നമ്പർ *</label>
                  <input className="form-input" type="tel" placeholder="98765 43210"
                    value={volunteerForm.phone} onChange={e => updateVolunteer('phone', e.target.value)} />
                  {volunteerErrors.phone && <div className="form-error">{volunteerErrors.phone}</div>}
                  <div className="form-hint">അഡ്മിൻ മാത്രം കാണും. പൊതുവിൽ കാണിക്കില്ല.</div>
                </div>

                <button className="btn btn-primary btn-full btn-lg" style={{ marginTop: 8 }} onClick={handleVolunteer}>
                  📲 അഡ്മിനെ അറിയിക്കൂ
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="section-header">
        <div>
          <div className="section-title">രക്ത അഭ്യർത്ഥനകൾ</div>
          <div className="section-sub">{loading ? '…' : `${requests.length} സജീവ അഭ്യർത്ഥനകൾ`}</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ അടക്കൂ' : '+ അഭ്യർത്ഥന ചേർക്കൂ'}
        </button>
      </div>

      {/* Request Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'Noto Serif Malayalam, serif', fontWeight: 700, fontSize: 17, marginBottom: 16 }}>🆘 രക്ത അഭ്യർത്ഥന</div>
          <div className="alert alert-info" style={{ marginBottom: 16 }}>
            <span>🔒</span>
            <span>നിങ്ങളുടെ ഫോൺ നമ്പർ പൊതു ഡയറക്ടറിയിൽ കാണിക്കില്ല. അഡ്മിൻ മാത്രം ബന്ധപ്പെടും.</span>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">രോഗിയുടെ പേര് *</label>
                <input className="form-input" type="text" placeholder="പൂർണ്ണ പേര്" value={form.patient_name} onChange={e => update('patient_name', e.target.value)} />
                {errors.patient_name && <div className="form-error">{errors.patient_name}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">രക്ത ഗ്രൂപ്പ് *</label>
                <select className="form-select" value={form.blood_group} onChange={e => update('blood_group', e.target.value)}>
                  <option value="">തിരഞ്ഞെടുക്കൂ</option>
                  {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                {errors.blood_group && <div className="form-error">{errors.blood_group}</div>}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">ആശുപത്രി *</label>
              <input className="form-input" type="text" placeholder="ആശുപത്രിയുടെ പേരും സ്ഥലവും" value={form.hospital} onChange={e => update('hospital', e.target.value)} />
              {errors.hospital && <div className="form-error">{errors.hospital}</div>}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">ബന്ധപ്പെടാൻ ഫോൺ *</label>
                <input className="form-input" type="tel" placeholder="98765 43210" value={form.contact_phone} onChange={e => update('contact_phone', e.target.value)} />
                {errors.contact_phone && <div className="form-error">{errors.contact_phone}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">യൂണിറ്റ്</label>
                <input className="form-input" type="number" min="1" max="10" value={form.units_needed} onChange={e => update('units_needed', Number(e.target.value))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">അടിയന്തരത</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {Object.entries(URGENCY_LABELS).map(([k, v]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13.5, padding: '6px 12px', borderRadius: 8, border: `1.5px solid ${form.urgency === k ? 'var(--red)' : 'var(--border)'}`, background: form.urgency === k ? 'var(--red-light)' : 'white', flex: 1, justifyContent: 'center' }}>
                    <input type="radio" name="urgency" value={k} checked={form.urgency === k} onChange={() => update('urgency', k)} style={{ display: 'none' }} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">അധിക വിവരം</label>
              <textarea className="form-textarea" placeholder="ഓർക്കേണ്ടതുണ്ടെങ്കിൽ ഇവിടെ…" value={form.message} onChange={e => update('message', e.target.value)} />
            </div>
            {errors.submit && <div className="alert alert-danger"><span>⚠️</span> {errors.submit}</div>}
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={submitLoading}>
              {submitLoading ? <><div className="spinner" style={{ width: 16, height: 16 }}></div> സമർപ്പിക്കുന്നു…</> : '📢 അഭ്യർത്ഥന പോസ്റ്റ് ചെയ്യൂ'}
            </button>
          </form>
        </div>
      )}

      {/* Active Requests */}
      {loading ? (
        <div className="loading"><div className="spinner"></div> ലോഡ് ചെയ്യുന്നു…</div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <div className="empty-title">നിലവിൽ അഭ്യർത്ഥനകളൊന്നുമില്ല</div>
          <div className="empty-sub">ആവശ്യം വരുമ്പോൾ ഇവിടെ ചേർക്കൂ</div>
        </div>
      ) : (
        <div>
          {requests.map(r => (
            <div key={r.id} className="request-card">
              <div className={`urgency-dot ${URGENCY_CLASSES[r.urgency]}`}></div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                  <div className="blood-badge">{r.blood_group}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{r.patient_name}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{timeAgo(r.created_at)}</div>
                  </div>
                  <span className="tag" style={{ marginLeft: 'auto' }}>{URGENCY_LABELS[r.urgency]}</span>
                </div>
                <div style={{ fontSize: 13.5, color: 'var(--text-2)', marginBottom: 4 }}>🏥 {r.hospital}</div>
                {r.units_needed > 1 && <div style={{ fontSize: 13, color: 'var(--text-3)' }}>📦 {r.units_needed} യൂണിറ്റ് ആവശ്യം</div>}
                {r.message && <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4, fontStyle: 'italic' }}>{r.message}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  {/* Big prominent volunteer button */}
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    onClick={() => { setVolunteerModal(r); setVolunteerSent(false); setVolunteerForm({ name: '', phone: '', blood_group: '' }); setVolunteerErrors({}); }}
                  >
                    🙋 എനിക്ക് ദാനം ചെയ്യാം
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => markFulfilled(r.id)}>
                    ✓ പൂർത്തിയായി
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
